import React, { memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/hooks/useTranslation";
import { Save, X } from "lucide-react";

interface EducationFormData {
  institution: string;
  degree: string;
  field_of_study: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  grade: string;
  activities: string;
  description: string;
}

interface EducationFormProps {
  formData: EducationFormData;
  onFormDataChange: (field: string, value: string | boolean) => void;
  onSave: (educationId?: string) => void;
  onCancel: () => void;
  educationId?: string;
}

export const EducationForm = memo(({ 
  formData, 
  onFormDataChange, 
  onSave, 
  onCancel, 
  educationId 
}: EducationFormProps) => {
  const { t } = useTranslation('ui');
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          value={formData.institution}
          onChange={(e) => onFormDataChange('institution', e.target.value)}
          placeholder={t('education_form.institution_placeholder')}
          required
        />
        <Input
          value={formData.degree}
          onChange={(e) => onFormDataChange('degree', e.target.value)}
          placeholder={t('education_form.degree_placeholder')}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          value={formData.field_of_study}
          onChange={(e) => onFormDataChange('field_of_study', e.target.value)}
          placeholder={t('education_form.field_placeholder')}
        />
        <Input
          value={formData.grade}
          onChange={(e) => onFormDataChange('grade', e.target.value)}
          placeholder={t('education_form.grade_placeholder')}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1 block">{t('education_form.start_date_label')}</label>
          <Input
            type="date"
            value={formData.start_date}
            onChange={(e) => onFormDataChange('start_date', e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">{t('education_form.end_date_label')}</label>
          <Input
            type="date"
            value={formData.end_date}
            onChange={(e) => onFormDataChange('end_date', e.target.value)}
            disabled={formData.is_current}
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="current-study"
          checked={formData.is_current}
          onCheckedChange={(checked) => {
            onFormDataChange('is_current', !!checked);
            if (checked) {
              onFormDataChange('end_date', '');
            }
          }}
        />
        <label htmlFor="current-study" className="text-sm">{t('education_form.currently_studying')}</label>
      </div>

      <Input
        value={formData.activities}
        onChange={(e) => onFormDataChange('activities', e.target.value)}
        placeholder={t('education_form.activities_placeholder')}
      />

      <Textarea
        value={formData.description}
        onChange={(e) => onFormDataChange('description', e.target.value)}
        placeholder={t('education_form.description_placeholder')}
        rows={3}
      />

      <div className="flex gap-2">
        <Button onClick={() => onSave(educationId)} size="sm">
          <Save className="w-4 h-4 mr-2" />
          {t('education_form.save')}
        </Button>
        <Button 
          onClick={onCancel} 
          variant="outline" 
          size="sm"
        >
          <X className="w-4 h-4 mr-2" />
          {t('education_form.cancel')}
        </Button>
      </div>
    </div>
  );
});

EducationForm.displayName = "EducationForm";